import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import { getCaptchaErrorMessage, getCaptchaSiteKey, Translation as T, useCaptcha, useTranslation } from "@awe-web/shared/lib";
import {
  Button,
  RequiredNotice,
  ScrollToError,
  FormikLabeledField,
  FormikTextInput,
  ErrorMessage
} from "../../components/form.js";

import "./request_reset_password_token_form.scss";
import { useSelector } from "react-redux";
import HCaptcha from '@hcaptcha/react-hcaptcha';

const INTRODUCTION = "FSD0000098";
const LABEL_EMAIL = "FSD0000099";
const PLACEHOLDER_EMAIL = "FSD0000100";
const BUTTON_SUBMIT = "FSD0000101";
const BUTTON_CANCEL = "FSD0000102";
const NOTICE = "FSD0000209";
const ERROR_ENTER_LOGIN = "FSD0000262";
const SUCCESS_MESSAGE = "FSD0000260";
const RESEND_EMAIL = "ALG0000164";

function ResetPasswordForm({ backlinkLocation, handleSubmit, onCaptchaChange }) {
  const { translate } = useTranslation();
  const { captchaRef, handleCaptcha, currentLanguage } = useCaptcha();
  const captchaPublicSiteKey = useSelector((state) => getCaptchaSiteKey(state));
  const resetErrorMessage = useSelector((state) => getCaptchaErrorMessage(state));

  const handleCaptchaChange = useCallback((token) => {
    onCaptchaChange(token);
    handleCaptcha(token);
  }, [handleCaptcha, onCaptchaChange]);

  return (
    <form onSubmit={handleSubmit}>
      <ScrollToError />
      <ErrorMessage error={resetErrorMessage} className="request-reset-password-token-form__error" />
      <div className="request-reset-password-token-form__fields">
        <FormikLabeledField
          className="request-reset-password-token-form__labeled-field"
          name="login"
          as={FormikTextInput}
          getFieldLabel={() => translate(LABEL_EMAIL)}
          isFieldRequired={() => true}
          email={true}
          placeholder={translate(PLACEHOLDER_EMAIL)}
        />
      </div>
      {captchaPublicSiteKey && <div className="request-reset-password-token-form__fields">
        <HCaptcha
          ref={captchaRef}
          sitekey={captchaPublicSiteKey}
          onVerify={handleCaptchaChange}
          hl={currentLanguage}
        />
      </div>
      }

      <Button primary continue submit className="request-reset-password-token-form__submit-button">
        <T code={BUTTON_SUBMIT} />
      </Button>

      <Button secondary back to={backlinkLocation} className="request-reset-password-token-form__submit-button">
        <T code={BUTTON_CANCEL} />
      </Button>

      <RequiredNotice className="request-reset-password-token-form__required-notice">
        <T code={NOTICE} />
      </RequiredNotice>
    </form>
  );
}

ResetPasswordForm.propTypes = {
  touched: PropTypes.object,
  errors: PropTypes.object,
  backlinkLocation: Button.propTypes.to,
  handleSubmit: PropTypes.func,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func
};

export default function RequestResetPasswordTokenForm({ children, backlinkLocation, onSubmit }) {

  const { translate } = useTranslation();

  const initialValues = {
    login: ""
  };

  const [submittedEmail, setSubmittedEmail] = useState(undefined);
  const [captchaToken, setCaptchaToken] = useState();
  const { captchaRef, handleCaptcha, currentLanguage } = useCaptcha();
  const captchaPublicSiteKey = useSelector((state) => getCaptchaSiteKey(state));
  const resetErrorMessage = useSelector((state) => getCaptchaErrorMessage(state));

  const handleSubmit = useCallback(async ({ login }) => {

    const result = await onSubmit({ login, captchaToken });
    if (result !== false) {
      setSubmittedEmail(login);
    }

  }, [captchaToken, onSubmit]);

  const onCaptchaChange = useCallback((captchaToken) => {
    setCaptchaToken(captchaToken);
  }, []);

  const handleCaptchaChange = useCallback((token) => {
    onCaptchaChange(token);
    handleCaptcha(token);
  }, [handleCaptcha, onCaptchaChange]);

  const validate = useCallback((values) => {
    const errors = {};

    if (!values.login) {
      errors.login = translate(ERROR_ENTER_LOGIN);
    }

    return errors;
  }, [translate]);

  if (submittedEmail) {
    return (
      <>
        <div className="request-reset-password-token-form__success-message">
          <T code={SUCCESS_MESSAGE} />
        </div>
        <ErrorMessage error={resetErrorMessage} className="request-reset-password-token-form__error" />
        {captchaPublicSiteKey && <div className="request-reset-password-token-form__fields">
          <HCaptcha
            ref={captchaRef}
            sitekey={captchaPublicSiteKey}
            onVerify={handleCaptchaChange}
            hl={currentLanguage}
          />
        </div>
        }
        <Button
          secondary
          onClick={() => handleSubmit({ login: submittedEmail })}
        >
          <T code={RESEND_EMAIL} />
        </Button>
      </>
    );
  } else {
    return (
      <div className="request-reset-password-token-form">
        <p className="request-reset-password-token-form__introduction">
          <T code={INTRODUCTION} />
        </p>

        <Formik
          validate={validate}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <ResetPasswordForm {...formikProps} backlinkLocation={backlinkLocation} onCaptchaChange={onCaptchaChange} />
          )}
        </Formik>

        {children}
      </div>
    );
  }
}

RequestResetPasswordTokenForm.propTypes = {
  children: PropTypes.node,
  defaultLogin: PropTypes.string,
  backlinkLocation: ResetPasswordForm.propTypes.backlinkLocation,
  onSubmit: PropTypes.func
};
